/* istanbul ignore file */
import type { IconNames } from '@kandji-inc/nectar-ui';
import { getEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';
import { i18n } from 'src/i18n';

export enum DeviceSelectValues {
  INDETERMINATE = 'INDETERMINATE',
  CHECKED = 'CHECKED',
  UNCHECKED = 'UNCHECKED',
}

export const CheckedValues = {
  [DeviceSelectValues.CHECKED]: true,
  [DeviceSelectValues.UNCHECKED]: false,
  [DeviceSelectValues.INDETERMINATE]: 'indeterminate',
};

export const NextCheckedValues = {
  [DeviceSelectValues.CHECKED]: DeviceSelectValues.UNCHECKED,
  [DeviceSelectValues.UNCHECKED]: DeviceSelectValues.CHECKED,
  [DeviceSelectValues.INDETERMINATE]: DeviceSelectValues.CHECKED,
};

export interface DeviceFilter {
  id: string;
  label: string;
  icon: IconNames;
  value: DeviceSelectValues;
  children?: DeviceFilter[];
  parent?: string;
  disabled?: boolean;
}

export interface DeviceFilterHandlers {
  updateFilters: (id: string, value: DeviceSelectValues) => void;
}

export type DeviceFamilyParent = (typeof DEVICE_FAMILY_PARENT)[number];

export type DeviceFamilyChildrenConfig = {
  [K in keyof typeof DEFAULT_DEVICE_FAMILY_CHILDREN_CONFIG]: boolean;
};

export type DeviceFamilyChildId = keyof DeviceFamilyChildrenConfig;

export type DeviceFamilyChildrenMap = {
  [K in keyof typeof DEVICE_FAMILY_CHILDREN_MAP]: (typeof DEVICE_FAMILY_CHILDREN_MAP)[K] & {
    parent: K;
  };
};

export const DEVICE_FAMILY_PARENT = ['apple', 'windows', 'android'] as const;

export const DEVICE_FAMILY_CHILDREN_MAP = {
  apple: ['mac', 'iphone', 'ipad', 'appletv', 'vision'],
  windows: [
    'windows_pc',
    'windows_phone',
    'windows_tablet',
    'windows_vr_headset',
  ],
  android: ['android_phone'],
} as const;

export const DEVICE_FAMILY_CHILDREN_FLAT = Object.values(
  DEVICE_FAMILY_CHILDREN_MAP,
).flat() as unknown as DeviceFamilyChildId[];

export const DEFAULT_DEVICE_FAMILY_CHILDREN_CONFIG = {
  mac: true,
  iphone: true,
  ipad: true,
  appletv: true,
  vision: true,
  windows_pc: false,
  windows_phone: false,
  windows_tablet: false,
  windows_vr_headset: false,
  android_phone: false,
};

export const DEVICE_FAMILY_ID_MAP = (label: string) => {
  const idMap = {
    [i18n.t('Apple')]: 'apple',
    [i18n.t('Mac')]: 'mac',
    [i18n.t('iPhone')]: 'iphone',
    [i18n.t('iPad')]: 'ipad',
    [i18n.t('Apple TV')]: 'appletv',
    [i18n.t('Vision')]: 'vision',
    [i18n.t('Windows')]: 'windows',
    [i18n.t('Android')]: 'android',
  };

  return idMap[label];
};

export const DEVICE_LABEL_RUNS_ON_MAP = (label: string) => {
  const labelMap = {
    [i18n.t('Mac')]: 'runsOnMac',
    [i18n.t('iPhone')]: 'runsOnIphone',
    [i18n.t('iPad')]: 'runsOnIpad',
    [i18n.t('Apple TV')]: 'runsOnTv',
    [i18n.t('Vision')]: 'runsOnVision',
    [i18n.t('Windows')]: 'runsOnWindows',
    [i18n.t('Android')]: 'runsOnAndroid',
  };

  return labelMap[label];
};

export const DEVICE_RUNS_ON_ID_MAP = (runsOn: string) => {
  const idMap = {
    [i18n.t('runsOnMac')]: 'mac',
    [i18n.t('runsOnIphone')]: 'iphone',
    [i18n.t('runsOnIpad')]: 'ipad',
    [i18n.t('runsOnTv')]: 'appletv',
    [i18n.t('runsOnVision')]: 'vision',
    [i18n.t('runsOnWindows')]: 'windows',
    [i18n.t('runsOnAndroid')]: 'android',
  };

  return idMap[runsOn];
};

export const APPLE_DEVICE_FAMILIES = (): DeviceFilter[] => [
  {
    id: 'mac',
    label: i18n.t('Mac'),
    icon: 'device-mac',
    value: DeviceSelectValues.UNCHECKED,
  },
  {
    id: 'iphone',
    label: i18n.t('iPhone'),
    icon: 'device-iphone',
    value: DeviceSelectValues.UNCHECKED,
  },
  {
    id: 'ipad',
    label: i18n.t('iPad'),
    icon: 'device-ipad',
    value: DeviceSelectValues.UNCHECKED,
  },
  {
    id: 'appletv',
    label: i18n.t('Apple TV'),
    icon: 'device-apple-tv',
    value: DeviceSelectValues.UNCHECKED,
  },
  {
    id: 'vision',
    label: i18n.t('Vision'),
    icon: 'device-vision',
    value: DeviceSelectValues.UNCHECKED,
  },
];

export const APPLE_GROUP_DEVICE_FAMILIES = (): DeviceFilter => ({
  id: 'apple',
  label: i18n.t('Apple'),
  icon: 'apple-logo',
  value: DeviceSelectValues.UNCHECKED,
  children: APPLE_DEVICE_FAMILIES().map((deviceFamily) => ({
    ...deviceFamily,
    parent: 'apple',
  })),
});

export const WINDOWS_DEVICE_FAMILY = (): DeviceFilter => ({
  id: 'windows',
  label: i18n.t('Windows'),
  icon: 'windows-logo',
  value: DeviceSelectValues.UNCHECKED,
});

export const ANDROID_DEVICE_FAMILY = (): DeviceFilter => ({
  id: 'android',
  label: i18n.t('Android'),
  icon: 'android-logo',
  value: DeviceSelectValues.UNCHECKED,
});

export const DEVICE_FAMILIES = (): DeviceFilter[] => {
  const { androidEnabled, windowsEnabled, crossPlatformEnabled } =
    getEnabledPlatforms();

  const deviceFamilies: DeviceFilter[] = [];

  if (crossPlatformEnabled) {
    deviceFamilies.push(APPLE_GROUP_DEVICE_FAMILIES());
  } else {
    deviceFamilies.push(...APPLE_DEVICE_FAMILIES());
  }

  if (windowsEnabled) {
    deviceFamilies.push(WINDOWS_DEVICE_FAMILY());
  }

  if (androidEnabled) {
    deviceFamilies.push(ANDROID_DEVICE_FAMILY());
  }

  return deviceFamilies;
};
